import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import MainLayout from "../../layouts/MainLayout"

import {
  createRole,
  deleteRole,
  getRoleById,
  getRoles,
  updateRole,
} from "../../services/roleService"


// =========================================================
// DEFAULT FORM
// =========================================================

const defaultFormData = {
  name: "",
  description: "",
}


// =========================================================
// PAGE
// =========================================================

function RolesPage() {
  const theme = useTheme()

  const isMdDown = useMediaQuery(
    theme.breakpoints.down("md")
  )

  const isSmDown = useMediaQuery(
    theme.breakpoints.down("sm")
  )


  // =======================================================
  // DATA
  // =======================================================

  const [roles, setRoles] = useState([])

  const [loading, setLoading] =
    useState(false)

  const [saving, setSaving] =
    useState(false)


  // =======================================================
  // DIALOG
  // =======================================================

  const [open, setOpen] =
    useState(false)

  const [selectedRole, setSelectedRole] =
    useState(null)

  const [formData, setFormData] =
    useState({
      ...defaultFormData,
    })


  // =======================================================
  // FILTER
  // =======================================================

  const [search, setSearch] =
    useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 10,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      severity: "success",
      message: "",
    })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const handleCloseSnackbar = () => {
    setSnackbar(
      (previous) => ({
        ...previous,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD ROLES
  // =======================================================

  const loadRoles = async () => {
    try {
      setLoading(true)

      const data =
        await getRoles()

      setRoles(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de charger les rôles.",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadRoles()
  }, [])


  // =======================================================
  // RESET PAGINATION AFTER SEARCH
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,
        page: 0,
      })
    )
  }, [search])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setSelectedRole(null)

    setFormData({
      ...defaultFormData,
    })
  }


  // =======================================================
  // OPEN ADD
  // =======================================================

  const openAddDialog = () => {
    resetForm()
    setOpen(true)
  }


  // =======================================================
  // OPEN EDIT
  // =======================================================

  const openEditDialog = async (id) => {
    try {
      const role =
        await getRoleById(id)

      setSelectedRole(role)

      setFormData({
        name:
          role.name || "",

        description:
          role.description || "",
      })

      setOpen(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de charger le rôle.",
        "error"
      )
    }
  }


  // =======================================================
  // CLOSE
  // =======================================================

  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target

    setFormData(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    )
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar(
        "Le nom du rôle est obligatoire.",
        "warning"
      )

      return
    }

    const payload = {
      name:
        formData.name.trim(),

      description:
        formData.description?.trim() ||
        null,
    }

    try {
      setSaving(true)

      if (selectedRole?.id) {
        await updateRole(
          selectedRole.id,
          payload
        )

        showSnackbar(
          "Rôle modifié avec succès."
        )
      } else {
        await createRole(payload)

        showSnackbar(
          "Rôle créé avec succès."
        )
      }

      await loadRoles()

      setOpen(false)
      resetForm()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible d'enregistrer le rôle.",
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete = async (id) => {
    const role =
      roles.find(
        (item) =>
          Number(item.id) ===
          Number(id)
      )

    const confirmed =
      window.confirm(
        `Supprimer le rôle "${
          role?.name || id
        }" ?`
      )

    if (!confirmed) {
      return
    }

    try {
      await deleteRole(id)

      showSnackbar(
        "Rôle supprimé avec succès."
      )

      await loadRoles()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          "Impossible de supprimer le rôle.",
        "error"
      )
    }
  }


  // =======================================================
  // FILTERED ROLES
  // =======================================================

  const filteredRoles =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return roles.filter(
        (item) => {
          return (
            keyword === "" ||
            String(
              item.name || ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.description || ""
            )
              .toLowerCase()
              .includes(keyword)
          )
        }
      )
    }, [
      roles,
      search,
    ])


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedRoles =
    useMemo(() => {
      const start =
        paginationModel.page *
        paginationModel.pageSize

      return filteredRoles.slice(
        start,
        start +
          paginationModel.pageSize
      )
    }, [
      filteredRoles,
      paginationModel,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredRoles.length /
            paginationModel.pageSize
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,
          page:
            totalPages - 1,
        })
      )
    }
  }, [
    filteredRoles.length,
    paginationModel.page,
    paginationModel.pageSize,
  ])


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "id",
          headerName: "ID",
          width: 65,
        },

        {
          field: "name",
          headerName: "Nom du rôle",
          minWidth: 185,
          flex: 0.85,

          renderCell: (params) => (
            <Typography
              variant="body2"
              fontWeight={800}
              noWrap
              title={
                params.value || ""
              }
            >
              {params.value || "-"}
            </Typography>
          ),
        },

        {
          field: "description",
          headerName: "Description",
          minWidth: 260,
          flex: 1.8,

          renderCell: (params) => (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              title={
                params.value || ""
              }
            >
              {params.value ||
                "Aucune description"}
            </Typography>
          ),
        },

        {
          field: "created_at",
          headerName: "Date de création",
          minWidth: 145,
          flex: 0.7,

          renderCell: (params) => (
            <Typography
              variant="body2"
              noWrap
            >
              {params.value
                ? new Date(
                    params.value
                  ).toLocaleDateString(
                    "fr-FR"
                  )
                : "-"}
            </Typography>
          ),
        },

        {
          field: "actions",
          headerName: "Actions",
          width: 185,

          sortable: false,
          filterable: false,
          disableColumnMenu: true,

          renderCell: (params) => (
            <Stack
              direction="row"
              spacing={0.75}
            >
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={() =>
                  openEditDialog(
                    params.row.id
                  )
                }
                sx={{
                  minWidth: 78,
                }}
              >
                Modifier
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() =>
                  handleDelete(
                    params.row.id
                  )
                }
                sx={{
                  minWidth: 86,
                }}
              >
                Supprimer
              </Button>
            </Stack>
          ),
        },
      ],
      [roles]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          width: "100%",

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },

            alignItems: "start",
            columnGap: 2,
            rowGap: 1.25,

            mb: 2.5,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Rôles
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.45,
                lineHeight: 1.55,
                maxWidth: 720,
              }}
            >
              Gérez les rôles utilisés pour
              organiser les droits et les
              responsabilités des utilisateurs.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation
            onClick={
              openAddDialog
            }
            sx={{
              minHeight: 42,

              px: 2.5,

              minWidth: {
                xs: "100%",
                sm: 160,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },

              boxShadow:
                "0 8px 18px rgba(37, 99, 235, 0.16)",
            }}
          >
            Ajouter un rôle
          </Button>
        </Box>


        {/* =================================================
            SEARCH
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 1.25,
              sm: 1.5,
            },

            mb: 2,

            borderRadius: 3.5,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1fr) auto",
              },

              gap: 1.25,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Rechercher par nom ou description..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
            />

            {search ? (
              <Button
                variant="outlined"
                onClick={() =>
                  setSearch("")
                }
                sx={{
                  minHeight: 40,

                  minWidth: {
                    xs: "100%",
                    sm: 120,
                  },
                }}
              >
                Réinitialiser
              </Button>
            ) : null}
          </Box>
        </Paper>


        {/* =================================================
            ROLES
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            minWidth: 0,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 10px 28px rgba(15, 23, 42, 0.04)",
          }}
        >

          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <Box
            sx={{
              px: {
                xs: 1.5,
                sm: 2,
              },

              py: 1.4,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 1,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des rôles
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Rôles disponibles pour les
                comptes RLSys Business
              </Typography>
            </Box>


            <Chip
              size="small"
              variant="outlined"
              label={`${filteredRoles.length} rôle${
                filteredRoles.length > 1
                  ? "s"
                  : ""
              }`}
              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,

                overflow: "hidden",

                bgcolor: "#ffffff",
              }}
            >
              <DataGrid
                autoHeight
                rows={filteredRoles}
                columns={columns}
                loading={loading}

                disableRowSelectionOnClick
                disableColumnSelector

                pagination

                paginationModel={
                  paginationModel
                }

                onPaginationModelChange={
                  setPaginationModel
                }

                pageSizeOptions={[
                  10,
                ]}

                rowHeight={58}

                columnHeaderHeight={50}

                localeText={{
                  noRowsLabel:
                    "Aucun rôle enregistré",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination: {
                    labelRowsPerPage:
                      "Lignes par page",

                    labelDisplayedRows: ({
                      from,
                      to,
                      count,
                    }) =>
                      `${from}–${to} sur ${count}`,
                  },
                }}

                sx={{
                  border: 0,

                  width: "100%",

                  minWidth: 0,

                  color: "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor: "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor: "#f8fafc",

                      px: 1.25,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight: 900,

                      color: "#334155",

                      fontSize:
                        "0.8rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      display: "flex",

                      alignItems:
                        "center",

                      minWidth: 0,

                      px: 1.25,

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight: 52,

                      bgcolor: "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 52,
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },

                  "& .MuiDataGrid-overlay":
                    {
                      bgcolor: "#ffffff",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE CARDS
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.25,
                  sm: 1.5,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des rôles...
                  </Typography>
                </Box>
              ) : paginatedRoles.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun rôle trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.25}
                >
                  {paginatedRoles.map(
                    (role) => (
                      <Paper
                        key={role.id}
                        variant="outlined"
                        sx={{
                          p: {
                            xs: 1.5,
                            sm: 1.75,
                          },

                          borderRadius: 3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={1.4}
                        >

                          {/* ROLE TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                                sx={{
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {role.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ID #{role.id}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                role.created_at
                                  ? new Date(
                                      role.created_at
                                    ).toLocaleDateString(
                                      "fr-FR"
                                    )
                                  : "—"
                              }
                              sx={{
                                fontWeight: 700,
                              }}
                            />
                          </Box>


                          {/* DESCRIPTION */}

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Description
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.25,

                                lineHeight: 1.55,

                                overflowWrap:
                                  "anywhere",
                              }}
                            >
                              {role.description ||
                                "Aucune description renseignée."}
                            </Typography>
                          </Box>


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs:
                                    "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              disableElevation
                              size="small"
                              onClick={() =>
                                openEditDialog(
                                  role.id
                                )
                              }
                            >
                              Modifier
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() =>
                                handleDelete(
                                  role.id
                                )
                              }
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  filteredRoles.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,
                    })
                  )
                }

                rowsPerPage={10}

                rowsPerPageOptions={[
                  10,
                ]}

                labelRowsPerPage="Lignes"

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor: "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      px: {
                        xs: 0.5,
                        sm: 1,
                      },

                      minHeight: 52,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          ADD / EDIT DIALOG
      ================================================= */}

      <Dialog
        open={open}

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedRole
              ? "Modifier le rôle"
              : "Ajouter un rôle"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.35,
            }}
          >
            {selectedRole
              ? "Modifiez le nom ou la description de ce rôle."
              : "Créez un nouveau rôle utilisable dans la gestion des utilisateurs."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}
            sx={{
              pt: 0.5,
            }}
          >
            {selectedRole ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 3,
                }}
              >
                Modification du rôle :{" "}
                <strong>
                  {selectedRole.name}
                </strong>
              </Alert>
            ) : null}


            <TextField
              size="small"

              label="Nom du rôle"

              name="name"

              value={
                formData.name
              }

              onChange={
                handleChange
              }

              fullWidth

              required

              disabled={
                saving
              }

              placeholder="Ex. responsable_stock"
            />


            <TextField
              label="Description"

              name="description"

              value={
                formData.description
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={4}

              disabled={
                saving
              }

              placeholder="Décrivez brièvement les responsabilités de ce rôle..."
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"

            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedRole
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default RolesPage